import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export type ConnectionStatus = 'none' | 'pending' | 'connected';

export function useConnectionStatus(targetUserId: string) {
  const { user } = useAuth();
  const [status, setStatus] = useState<ConnectionStatus>('none');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    if (!user || !targetUserId || user.id === targetUserId) return;
    setLoading(true);
    setError(null);
    const { data, error } = await supabase.rpc('get_connection_status', { target_user: targetUserId });
    if (error) setError('Error fetching connection status');
    setStatus((data as ConnectionStatus) || 'none');
    setLoading(false);
  }, [user, targetUserId]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const sendRequest = useCallback(async () => {
    if (!user || !targetUserId) return;
    setLoading(true);
    setError(null);
    const { error } = await supabase.rpc('send_connection_request', { target_user: targetUserId });
    if (error) {
      if (error.code === '409') {
        setError('Request already sent or connection already exists.');
        await fetchStatus();
      } else {
        setError('Error sending connection request');
      }
    } else {
      await fetchStatus();
    }
    setLoading(false);
  }, [user, targetUserId, fetchStatus]);

  return { status, loading, error, sendRequest, refetch: fetchStatus };
} 